/**
 * Full-screen modal for previewing AI-generated documents and images.
 * Shows the content with Save (keep), Discard (delete note + blob), and
 * Open in New Tab (for documents — allows printing to PDF).
 */

import { useState, useCallback } from 'preact/hooks'
import type { GeneratedContent } from '../types'
import { deleteCloudNote, getBlobDirectUrl, downloadBlobAsUrl, downloadBlobAsText } from '../services/api'
import { ReportContentDialog } from './ReportContentDialog'

interface GeneratedContentPreviewProps {
  content: GeneratedContent
  onClose: () => void
  /** The user's latest chat message; offered for inclusion in reports. */
  userRequest?: string | null
}

export function GeneratedContentPreview({ content, onClose, userRequest }: GeneratedContentPreviewProps) {
  const [discarding, setDiscarding] = useState(false)
  const [reportOpen, setReportOpen] = useState(false)
  const [reportSent, setReportSent] = useState(false)
  /** Full document text for reports; content_preview is only the start. */
  const [documentText, setDocumentText] = useState<string | null>(null)
  const [imageUrl, setImageUrl] = useState<string | null>(null)
  const [imageLoading, setImageLoading] = useState(content.content_type === 'image')

  // Load image from blob on mount
  if (content.content_type === 'image' && content.blob_key && !imageUrl && imageLoading) {
    downloadBlobAsUrl(content.blob_key).then(result => {
      if (result) setImageUrl(result.url)
      setImageLoading(false)
    }).catch(() => setImageLoading(false))
  }

  const handleSave = useCallback(() => {
    // Content is already saved as a note — just close the preview
    onClose()
  }, [onClose])

  const handleDiscard = useCallback(async () => {
    setDiscarding(true)
    try {
      // Delete the pre-saved note (cascade deletes blob reference)
      await deleteCloudNote(content.note_id)
      // TODO: also delete blob from R2 if needed (backend may not cascade)
    } catch (e) {
      console.warn('Failed to discard generated content:', e)
    }
    setDiscarding(false)
    onClose()
  }, [content.note_id, onClose])

  const handleOpenInNewTab = useCallback(async () => {
    if (content.content_type === 'document' && content.blob_key) {
      const url = await getBlobDirectUrl(content.blob_key)
      if (url) {
        window.open(url, '_blank')
        return
      }
    }
    // Fallback: create blob URL from content_preview
    if (content.content_preview) {
      const blob = new Blob([content.content_preview], { type: content.mime_type || 'text/html' })
      const url = URL.createObjectURL(blob)
      window.open(url, '_blank')
    }
  }, [content])

  const isDocument = content.content_type === 'document'
  const isImage = content.content_type === 'image'

  const handleReport = useCallback(async () => {
    if (isDocument && content.blob_key && documentText === null) {
      try {
        const text = await downloadBlobAsText(content.blob_key)
        if (text) setDocumentText(text)
      } catch (e) {
        console.warn('Could not load full document for report:', e)
      }
    }
    setReportOpen(true)
  }, [isDocument, content.blob_key, documentText])

  return (
    <div style={overlayStyle} onClick={handleSave}>
      <div style={modalStyle} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={headerStyle}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 16, fontWeight: 600 }}>
              {isDocument ? '\uD83D\uDCC4' : '\uD83D\uDDBC\uFE0F'}{' '}
              {isDocument ? (content.subject || 'Generated Document') : (content.image_name || 'Generated Image')}
            </div>
            {isDocument && content.display_format && (
              <div style={{ fontSize: 12, color: 'rgba(250,250,250,0.5)', marginTop: 4 }}>
                {content.display_format} document
                {content.document_type ? ` \u2022 ${content.document_type}` : ''}
                {content.is_app ? ' \u2022 Interactive App' : ''}
              </div>
            )}
            {isImage && (
              <div style={{ fontSize: 12, color: 'rgba(250,250,250,0.5)', marginTop: 4 }}>
                {content.model || 'AI'} \u2022 {content.aspect_ratio || '1:1'}
              </div>
            )}
          </div>
          <button style={closeBtnStyle} onClick={handleSave}>{'\u2715'}</button>
        </div>

        {/* Content area */}
        <div style={contentAreaStyle}>
          {isDocument && content.content_preview && (
            content.format === 'html' ? (
              <iframe
                srcDoc={content.content_preview}
                style={{ width: '100%', height: '100%', border: 'none', borderRadius: 6, background: '#fff' }}
                sandbox="allow-same-origin"
                title="Document preview"
              />
            ) : (
              <pre style={previewTextStyle}>
                {content.content_preview}
              </pre>
            )
          )}

          {isImage && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 16 }}>
              {imageLoading ? (
                <div style={{ color: 'rgba(250,250,250,0.5)' }}>Loading image...</div>
              ) : imageUrl ? (
                <img src={imageUrl} alt={content.image_name || 'Generated image'} style={{ maxWidth: '100%', maxHeight: '100%', borderRadius: 8, objectFit: 'contain' }} />
              ) : (
                <div style={{ color: 'rgba(250,250,250,0.5)' }}>Image not available for preview</div>
              )}
              {content.prompt && (
                <div style={{ fontSize: 12, color: 'rgba(250,250,250,0.5)', textAlign: 'center', maxWidth: 600 }}>
                  {content.revised_prompt && content.revised_prompt !== content.prompt
                    ? content.revised_prompt
                    : content.prompt
                  }
                </div>
              )}
            </div>
          )}
        </div>

        {/* Summary for documents */}
        {isDocument && content.summary && (
          <div style={{ padding: '8px 20px', fontSize: 13, color: 'rgba(250,250,250,0.6)', borderTop: '1px solid rgba(250,250,250,0.1)' }}>
            {content.summary}
          </div>
        )}

        {/* Action buttons */}
        <div style={footerStyle}>
          <button style={discardBtnStyle} onClick={handleDiscard} disabled={discarding}>
            {discarding ? 'Discarding...' : '\uD83D\uDDD1\uFE0F Discard'}
          </button>
          {/* Report offensive AI output in-app (Google Play AI content policy) */}
          <button
            style={actionBtnStyle}
            onClick={handleReport}
            disabled={reportSent}
            title="Report inappropriate content"
          >
            {reportSent ? '✓ Reported' : '🚩 Report'}
          </button>
          <div style={{ flex: 1 }} />
          {isDocument && (
            <button style={actionBtnStyle} onClick={handleOpenInNewTab}>
              {'\uD83D\uDD17'} Open in New Tab
            </button>
          )}
          <button style={saveBtnStyle} onClick={handleSave}>
            {'\u2705'} Save to Notes
          </button>
        </div>
        {reportOpen && (
          <ReportContentDialog
            kind={isImage ? 'image' : 'document'}
            aiContent={
              isImage
                ? (content.revised_prompt && content.revised_prompt !== content.prompt
                    ? `${content.prompt || ''}\n\nRevised: ${content.revised_prompt}`
                    : (content.prompt || content.image_name || ''))
                : `${content.subject || ''}\n\n${documentText ?? content.content_preview ?? content.summary ?? ''}`
            }
            userRequest={userRequest}
            imageUrl={isImage ? imageUrl : null}
            messageId={content.note_id}
            onClose={(sent) => {
              setReportOpen(false)
              if (sent) setReportSent(true)
            }}
          />
        )}
      </div>
    </div>
  )
}

// ── Styles ──────────────────────────────────────────────────────

const overlayStyle: Record<string, string | number> = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: 'rgba(0,0,0,0.7)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
}

const modalStyle: Record<string, string | number> = {
  width: '90vw',
  maxWidth: 1000,
  height: '85vh',
  background: '#002233',
  borderRadius: 12,
  border: '1px solid rgba(250,250,250,0.15)',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
}

const headerStyle: Record<string, string | number> = {
  padding: '16px 20px',
  borderBottom: '1px solid rgba(250,250,250,0.1)',
  display: 'flex',
  alignItems: 'flex-start',
  gap: 12,
  flexShrink: 0,
}

const closeBtnStyle: Record<string, string | number> = {
  background: 'transparent',
  border: 'none',
  color: 'rgba(250,250,250,0.5)',
  fontSize: 18,
  cursor: 'pointer',
  padding: '4px 8px',
}

const contentAreaStyle: Record<string, string | number> = {
  flex: 1,
  overflow: 'auto',
  padding: 20,
  minHeight: 0,
}

const previewTextStyle: Record<string, string | number> = {
  margin: 0,
  padding: 16,
  background: 'rgba(0,51,68,0.6)',
  borderRadius: 8,
  fontSize: 13,
  lineHeight: '1.6',
  color: 'rgba(250,250,250,0.8)',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
  fontFamily: 'monospace',
  overflow: 'auto',
  maxHeight: '100%',
}

const footerStyle: Record<string, string | number> = {
  padding: '12px 20px',
  borderTop: '1px solid rgba(250,250,250,0.1)',
  display: 'flex',
  gap: 10,
  alignItems: 'center',
  flexShrink: 0,
}

const discardBtnStyle: Record<string, string | number> = {
  padding: '8px 20px',
  border: '1px solid rgba(255,100,100,0.3)',
  borderRadius: 8,
  background: 'rgba(255,68,68,0.1)',
  color: '#ff6b6b',
  cursor: 'pointer',
  fontSize: 13,
  fontWeight: 500,
}

const actionBtnStyle: Record<string, string | number> = {
  padding: '8px 20px',
  border: '1px solid rgba(250,250,250,0.15)',
  borderRadius: 8,
  background: 'transparent',
  color: 'rgba(250,250,250,0.8)',
  cursor: 'pointer',
  fontSize: 13,
  fontWeight: 500,
}

const saveBtnStyle: Record<string, string | number> = {
  padding: '8px 24px',
  border: 'none',
  borderRadius: 8,
  background: 'rgba(45,165,160,0.3)',
  color: '#fff',
  cursor: 'pointer',
  fontSize: 13,
  fontWeight: 600,
}
